import React, { FC, useState, useEffect, useCallback } from 'react';
import { createStyles, makeStyles, Table, TableBody, TableHead, TableRow, Typography } from '@material-ui/core';
import axios, { CancelTokenSource } from 'axios';

import CreateEditJobActionTagTemplateForm from './components/CreateEditJobActionTagTemplateForm';

import HeaderRow from 'components/HeaderRow';
import BodyCell from 'components/BodyCell';
import BodyRow from './components/BodyRow';
import TablePagination from 'components/TablePagination';
import { dummyJobActionTagTemplate } from 'constants/dummy';
import { JOB_ACTION_TAG_TEMPLATE_BASE_URL, GET_EDIT_JOB_ACTION_TAG_TEMPLATE_URL } from 'constants/url';

interface Props {
  isLoadingData: boolean;
  jobActionTagTemplates: JobActionTagTemplateModel[];
  count: number;
  currentPage: number;
  rowsPerPage: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  handleChangeRowsPerPage: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  openCreateJobActionTagTemplate: boolean;
  handleCancelCreateJobActionTagTemplate(): void;
  openEditJobActionTagTemplate: boolean;
  jobActionTagTemplate?: JobActionTagTemplateModel;
  currentEditingJobActionTagTemplateIndex: number;
  handleOpenEditJobActionTagTemplate: (jobActionTagtemplateIndex: number) => React.MouseEventHandler;
  handleCancelEditJobActionTagTemplate(): void;
  addNewJobActionTagTemplate(jobActionTagtemplate: JobActionTagTemplateModel): void;
  updateIndividualJobActionTagTemplate: (updatedJobActionTagTemplateProperties: Partial<JobActionTagTemplateModel>) => void;
  deleteIndividualJobActionTagTemplate: (jobActionTagtemplateIndex: number) => void;
  onReorderJobActionTagTemplate: (jobActionTagTemplateId: number, direction: 'up' | 'down') => void;
  handleSnackbar: (variant: 'success' | 'error', message: string) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    tableWrapper: {
      overflowX: 'auto'
    }
  })
);

const JobActionTagTemplateTable: FC<Props> = props => {
  const classes = useStyles();
  let cancelTokenSource: CancelTokenSource;

  const {
    isLoadingData,
    jobActionTagTemplates,
    count,
    currentPage,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    openCreateJobActionTagTemplate,
    handleCancelCreateJobActionTagTemplate,
    openEditJobActionTagTemplate,
    jobActionTagTemplate,
    currentEditingJobActionTagTemplateIndex,
    handleOpenEditJobActionTagTemplate,
    handleCancelEditJobActionTagTemplate,
    addNewJobActionTagTemplate,
    updateIndividualJobActionTagTemplate,
    deleteIndividualJobActionTagTemplate,
    onReorderJobActionTagTemplate,
    handleSnackbar
  } = props;

  const [isLoading, setLoading] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const resetInputFormValues = () => {
    setName('');
    setDescription('');
  };

  const resetEditFormValues = useCallback(() => {
    if (!jobActionTagTemplate) {
      return;
    }

    const { name, description } = jobActionTagTemplate;

    setName(name);
    setDescription(description);
  }, [jobActionTagTemplate]);

  const clearFormErrors = () => {
    setNameError('');
  };

  // The below logic introduces a 500ms delay for showing the skeleton
  const [showSkeleton, setShowSkeleton] = useState<boolean>(false);
  useEffect(() => {
    if (!openEditJobActionTagTemplate) {
      if (isLoadingData) {
        setShowSkeleton(true);
      }

      resetInputFormValues();
      clearFormErrors();

      return () => {
        setShowSkeleton(false);
      };
    } else {
      resetEditFormValues();
      clearFormErrors();
    }
  }, [openEditJobActionTagTemplate, isLoadingData, resetEditFormValues]);

  const validateForm = () => {
    let ret = true;
    clearFormErrors();

    if (!name || !name.trim()) {
      setNameError('Please enter action tag name');
      ret = false;
    }

    return ret;
  };

  const handleCloseCreateJobActionTagTemplate = () => {
    handleCancelCreateJobActionTagTemplate();
    resetInputFormValues();
    clearFormErrors();
  };

  const handleCloseEditJobActionTagTemplate = () => {
    handleCancelEditJobActionTagTemplate();
    resetInputFormValues();
    clearFormErrors();
  };

  const handleOnSubmit: React.FormEventHandler = async event => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      cancelTokenSource = axios.CancelToken.source();
      if (!openEditJobActionTagTemplate) {
        const response = await axios.post(
          `${JOB_ACTION_TAG_TEMPLATE_BASE_URL}`,
          {
            name,
            description
          },
          { cancelToken: cancelTokenSource.token }
        );
        addNewJobActionTagTemplate(response.data);
        handleSnackbar('success', 'Successfully added new job action tag template');
      } else {
        const response = await axios.put(
          `${GET_EDIT_JOB_ACTION_TAG_TEMPLATE_URL(jobActionTagTemplate!.id)}`,
          {
            name,
            description
          },
          { cancelToken: cancelTokenSource.token }
        );
        updateIndividualJobActionTagTemplate(response.data);
        handleSnackbar('success', 'Successfully edited job action tag template');
      }
      !openEditJobActionTagTemplate ? handleCancelCreateJobActionTagTemplate() : handleCancelEditJobActionTagTemplate();
    } catch (err) {
      handleSnackbar('error', !openEditJobActionTagTemplate ? 'Failed to add job action tag template' : 'Failed to edit job action tag template');
    }

    setLoading(false);
  };

  return (
    <div className={classes.tableWrapper}>
      <Table>
        <TableHead>
          <HeaderRow
            headers={[{ label: 'Action Tag' }, { label: 'Description' }, { label: 'Action', pL: '110px', pR: '0px', pT: '0px', pB: '0px' }]}
          />
        </TableHead>
        <TableBody>
          {openCreateJobActionTagTemplate && (
            <CreateEditJobActionTagTemplateForm
              setName={setName}
              nameError={nameError}
              setNameError={setNameError}
              setDescription={setDescription}
              isSubmitting={isLoading}
              onSubmit={handleOnSubmit}
              onCancel={handleCloseCreateJobActionTagTemplate}
              primaryButtonLabel={'Save'}
            />
          )}
          {showSkeleton ? (
            [1, 2, 3, 4, 5].map(index => (
              <BodyRow
                index={index}
                key={index}
                jobActionTagTemplate={dummyJobActionTagTemplate}
                onEditJobActionTagTemplate={handleOpenEditJobActionTagTemplate(index)}
                isLoadingData={showSkeleton}
                deleteIndividualJobActionTagTemplate={deleteIndividualJobActionTagTemplate}
                onReorderJobActionTagTemplate={onReorderJobActionTagTemplate}
                isFirstItem={index === 1}
                isLastItem={index === 5}
                handleSnackbar={handleSnackbar}
              />
            ))
          ) : jobActionTagTemplates.length > 0 ? (
            jobActionTagTemplates.map((jobActionTagTemplate, index) =>
              openEditJobActionTagTemplate && currentEditingJobActionTagTemplateIndex === index ? (
                <CreateEditJobActionTagTemplateForm
                  key={jobActionTagTemplate.id}
                  jobActionTagTemplate={jobActionTagTemplate}
                  setName={setName}
                  nameError={nameError}
                  setNameError={setNameError}
                  setDescription={setDescription}
                  isSubmitting={isLoading}
                  onSubmit={handleOnSubmit}
                  onCancel={handleCloseEditJobActionTagTemplate}
                  primaryButtonLabel={'Save'}
                  customBackground={'#F4F9FC'}
                />
              ) : (
                <BodyRow
                  index={index}
                  key={jobActionTagTemplate.id}
                  jobActionTagTemplate={jobActionTagTemplate}
                  onEditJobActionTagTemplate={handleOpenEditJobActionTagTemplate(index)}
                  isLoadingData={showSkeleton}
                  deleteIndividualJobActionTagTemplate={deleteIndividualJobActionTagTemplate}
                  onReorderJobActionTagTemplate={onReorderJobActionTagTemplate}
                  isFirstItem={currentPage * rowsPerPage + index === 0}
                  isLastItem={currentPage * rowsPerPage + index === count - 1}
                  handleSnackbar={handleSnackbar}
                />
              )
            )
          ) : (
            <TableRow>
              <BodyCell colSpan={4}>
                <Typography variant='body2' color='textSecondary' style={{ textAlign: 'center' }}>
                  No matching result
                </Typography>
              </BodyCell>
            </TableRow>
          )}
        </TableBody>
        <TablePagination
          rowsPerPageOptions={[5, 10, 15]}
          count={count}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Table>
    </div>
  );
};

export default JobActionTagTemplateTable;
